// src/skills/glyphbinding/glyphbindingData.js
// Data definitions for the Glyphbinding skill

/**
 * Ichor vats (gathering spots) - like mining rocks but for ichor
 * Higher level vats give more ichor per gather
 */
export const ESSENCE_NODES = {
    ARCANE: {
        id: 'arcane_residue_vat',
        name: 'Arcane Residue Vat',
        level: 1,
        xp: 5,
        baseTime: 2000,
        color: '#E8E8E8',
        drops: [{ itemId: 'arcane_residue', weight: 1, minQuantity: 1, maxQuantity: 2 }]
    },
    REFINED: {
        id: 'refined_ichor_vat',
        name: 'Refined Ichor Vat',
        level: 35,
        xp: 12,
        baseTime: 3000,
        color: '#9B59B6',
        drops: [{ itemId: 'refined_arcane_ichor', weight: 1, minQuantity: 2, maxQuantity: 4 }]
    },
    PURIFIED: {
        id: 'purified_core_vat',
        name: 'Purified Core Vat',
        level: 70,
        xp: 25,
        baseTime: 4000,
        color: '#00CED1',
        drops: [{ itemId: 'purified_arcane_core', weight: 1, minQuantity: 3, maxQuantity: 5 }]
    }
};

/**
 * Glyph recipes - crafted from ichor
 * 12 glyphs total - all serve dual purpose for combat spells AND glyph cores
 */
export const GLYPH_RECIPES = {
    // ============================================
    // ELEMENTAL GLYPHS - Arcane Residue (1 each)
    // ============================================
    STORM: {
        id: 'storm_glyph',
        name: 'Storm Glyph',
        level: 1,
        xp: 5,
        baseTime: 2000,
        color: '#87CEEB',
        inputItem: 'arcane_residue',
        inputQuantity: 1,
        drops: [{ itemId: 'storm_glyph', quantity: 1 }]
    },
    FROST: {
        id: 'frost_glyph',
        name: 'Frost Glyph',
        level: 5,
        xp: 6,
        baseTime: 2000,
        color: '#1E90FF',
        inputItem: 'arcane_residue',
        inputQuantity: 1,
        drops: [{ itemId: 'frost_glyph', quantity: 1 }]
    },
    STONE: {
        id: 'stone_glyph',
        name: 'Stone Glyph',
        level: 9,
        xp: 7,
        baseTime: 2000,
        color: '#8B4513',
        inputItem: 'arcane_residue',
        inputQuantity: 1,
        drops: [{ itemId: 'stone_glyph', quantity: 1 }]
    },
    FLAME: {
        id: 'flame_glyph',
        name: 'Flame Glyph',
        level: 14,
        xp: 8,
        baseTime: 2000,
        color: '#FF4500',
        inputItem: 'arcane_residue',
        inputQuantity: 1,
        drops: [{ itemId: 'flame_glyph', quantity: 1 }]
    },

    // ============================================
    // ASPECT GLYPH - Arcane Residue (2 each)
    // ============================================
    VIGOR: {
        id: 'vigor_glyph',
        name: 'Vigor Glyph',
        level: 25,
        xp: 18,
        baseTime: 2500,
        color: '#4169E1',
        inputItem: 'arcane_residue',
        inputQuantity: 2,
        drops: [{ itemId: 'vigor_glyph', quantity: 1 }]
    },

    // ============================================
    // PRIMAL GLYPHS - Refined Arcane Ichor
    // ============================================
    VERDANT: {
        id: 'verdant_glyph',
        name: 'Verdant Glyph',
        level: 35,
        xp: 25,
        baseTime: 3000,
        color: '#228B22',
        inputItem: 'refined_arcane_ichor',
        inputQuantity: 1,
        drops: [{ itemId: 'verdant_glyph', quantity: 1 }]
    },
    ENTROPY: {
        id: 'entropy_glyph',
        name: 'Entropy Glyph',
        level: 44,
        xp: 30,
        baseTime: 3000,
        color: '#FF6347',
        inputItem: 'refined_arcane_ichor',
        inputQuantity: 1,
        drops: [{ itemId: 'entropy_glyph', quantity: 1 }]
    },

    // ============================================
    // COSMIC GLYPHS - Refined Arcane Ichor (2 each)
    // ============================================
    VOID: {
        id: 'void_glyph',
        name: 'Void Glyph',
        level: 55,
        xp: 40,
        baseTime: 3500,
        color: '#9932CC',
        inputItem: 'refined_arcane_ichor',
        inputQuantity: 2,
        drops: [{ itemId: 'void_glyph', quantity: 1 }]
    },
    BINDING: {
        id: 'binding_glyph',
        name: 'Binding Glyph',
        level: 65,
        xp: 50,
        baseTime: 3500,
        color: '#FFD700',
        inputItem: 'refined_arcane_ichor',
        inputQuantity: 2,
        drops: [{ itemId: 'binding_glyph', quantity: 1 }]
    },

    // ============================================
    // FORBIDDEN GLYPHS - Purified Arcane Core
    // ============================================
    DOOM: {
        id: 'doom_glyph',
        name: 'Doom Glyph',
        level: 75,
        xp: 60,
        baseTime: 4000,
        color: '#2F4F4F',
        inputItem: 'purified_arcane_core',
        inputQuantity: 1,
        drops: [{ itemId: 'doom_glyph', quantity: 1 }]
    },
    SANGUINE: {
        id: 'sanguine_glyph',
        name: 'Sanguine Glyph',
        level: 85,
        xp: 75,
        baseTime: 4000,
        color: '#8B0000',
        inputItem: 'purified_arcane_core',
        inputQuantity: 2,
        drops: [{ itemId: 'sanguine_glyph', quantity: 1 }]
    },
    SPIRIT: {
        id: 'spirit_glyph',
        name: 'Spirit Glyph',
        level: 95,
        xp: 100,
        baseTime: 5000,
        color: '#E6E6FA',
        inputItem: 'purified_arcane_core',
        inputQuantity: 3,
        drops: [{ itemId: 'spirit_glyph', quantity: 1 }]
    }
};

/**
 * Glyph Core recipes - magical orbs crafted from glyphs
 * Combined with logs in Woodworking to create staffs
 * Uses inputItems array for multi-material recipes
 * All glyphs used here are also used in combat spells (no wasted crafting)
 */
export const GLYPH_CORE_RECIPES = {
    COMMON: {
        id: 'common_glyph_core',
        name: 'Common Glyph Core',
        level: 10,
        xp: 25,
        baseTime: 3000,
        color: '#DEB887',
        inputItems: [
            { itemId: 'storm_glyph', quantity: 2 },
            { itemId: 'stone_glyph', quantity: 2 },
            { itemId: 'arcane_residue', quantity: 5 }
        ],
        drops: [{ itemId: 'common_glyph_core', quantity: 1 }]
    },
    IRONWOOD: {
        id: 'ironwood_glyph_core',
        name: 'Ironwood Glyph Core',
        level: 20,
        xp: 45,
        baseTime: 3500,
        color: '#A0522D',
        inputItems: [
            { itemId: 'flame_glyph', quantity: 3 },
            { itemId: 'frost_glyph', quantity: 3 },
            { itemId: 'arcane_residue', quantity: 8 }
        ],
        drops: [{ itemId: 'ironwood_glyph_core', quantity: 1 }]
    },
    SOFTBARK: {
        id: 'softbark_glyph_core',
        name: 'Softbark Glyph Core',
        level: 30,
        xp: 70,
        baseTime: 4000,
        color: '#6B8E23',
        inputItems: [
            { itemId: 'vigor_glyph', quantity: 4 },
            { itemId: 'arcane_residue', quantity: 12 }
        ],
        drops: [{ itemId: 'softbark_glyph_core', quantity: 1 }]
    },
    GOLDLEAF: {
        id: 'goldleaf_glyph_core',
        name: 'Goldleaf Glyph Core',
        level: 45,
        xp: 100,
        baseTime: 4500,
        color: '#D2691E',
        inputItems: [
            { itemId: 'verdant_glyph', quantity: 3 },
            { itemId: 'entropy_glyph', quantity: 2 },
            { itemId: 'refined_arcane_ichor', quantity: 5 }
        ],
        drops: [{ itemId: 'goldleaf_glyph_core', quantity: 1 }]
    },
    REDGRAIN: {
        id: 'redgrain_glyph_core',
        name: 'Redgrain Glyph Core',
        level: 55,
        xp: 130,
        baseTime: 5000,
        color: '#CD853F',
        inputItems: [
            { itemId: 'void_glyph', quantity: 2 },
            { itemId: 'entropy_glyph', quantity: 3 },
            { itemId: 'refined_arcane_ichor', quantity: 8 }
        ],
        drops: [{ itemId: 'redgrain_glyph_core', quantity: 1 }]
    },
    GRAVEWOOD: {
        id: 'gravewood_glyph_core',
        name: 'Gravewood Glyph Core',
        level: 70,
        xp: 165,
        baseTime: 5500,
        color: '#228B22',
        inputItems: [
            { itemId: 'binding_glyph', quantity: 3 },
            { itemId: 'doom_glyph', quantity: 2 },
            { itemId: 'purified_arcane_core', quantity: 5 }
        ],
        drops: [{ itemId: 'gravewood_glyph_core', quantity: 1 }]
    },
    ARCANEWOOD: {
        id: 'arcanewood_glyph_core',
        name: 'Arcanewood Glyph Core',
        level: 85,
        xp: 200,
        baseTime: 6000,
        color: '#9370DB',
        inputItems: [
            { itemId: 'sanguine_glyph', quantity: 3 },
            { itemId: 'spirit_glyph', quantity: 2 },
            { itemId: 'purified_arcane_core', quantity: 8 }
        ],
        drops: [{ itemId: 'arcanewood_glyph_core', quantity: 1 }]
    }
};

/**
 * Focus recipes - magic offhand items crafted from glyph cores + ichor
 * Each tier uses a matching glyph core tier and ichor type
 */
export const FOCUS_RECIPES = {
    STORM: {
        id: 'storm_focus',
        name: 'Storm Focus',
        level: 15,
        xp: 35,
        baseTime: 4000,
        color: '#87CEEB',
        inputItems: [
            { itemId: 'common_glyph_core', quantity: 1 },
            { itemId: 'arcane_residue', quantity: 3 }
        ],
        drops: [{ itemId: 'storm_focus', quantity: 1 }],
        tier: 1
    },
    FROST: {
        id: 'frost_focus',
        name: 'Frost Focus',
        level: 30,
        xp: 60,
        baseTime: 5000,
        color: '#1E90FF',
        inputItems: [
            { itemId: 'ironwood_glyph_core', quantity: 1 },
            { itemId: 'arcane_residue', quantity: 5 }
        ],
        drops: [{ itemId: 'frost_focus', quantity: 1 }],
        tier: 2
    },
    STONE: {
        id: 'stone_focus',
        name: 'Stone Focus',
        level: 45,
        xp: 90,
        baseTime: 6000,
        color: '#8B4513',
        inputItems: [
            { itemId: 'softbark_glyph_core', quantity: 1 },
            { itemId: 'refined_arcane_ichor', quantity: 3 }
        ],
        drops: [{ itemId: 'stone_focus', quantity: 1 }],
        tier: 3
    },
    FLAME: {
        id: 'flame_focus',
        name: 'Flame Focus',
        level: 60,
        xp: 120,
        baseTime: 7000,
        color: '#FF4500',
        inputItems: [
            { itemId: 'goldleaf_glyph_core', quantity: 1 },
            { itemId: 'refined_arcane_ichor', quantity: 5 }
        ],
        drops: [{ itemId: 'flame_focus', quantity: 1 }],
        tier: 4
    },
    VOID: {
        id: 'void_focus',
        name: 'Void Focus',
        level: 75,
        xp: 155,
        baseTime: 8000,
        color: '#9932CC',
        inputItems: [
            { itemId: 'gravewood_glyph_core', quantity: 1 },
            { itemId: 'purified_arcane_core', quantity: 3 }
        ],
        drops: [{ itemId: 'void_focus', quantity: 1 }],
        tier: 5
    },
    SPIRIT: {
        id: 'spirit_focus',
        name: 'Spirit Focus',
        level: 90,
        xp: 200,
        baseTime: 9000,
        color: '#E6E6FA',
        inputItems: [
            { itemId: 'arcanewood_glyph_core', quantity: 1 },
            { itemId: 'purified_arcane_core', quantity: 5 }
        ],
        drops: [{ itemId: 'spirit_focus', quantity: 1 }],
        tier: 6
    }
};

/**
 * Tool definitions for glyphbinding (chisels)
 */
export const CHISELS = {
    BASIC: {
        id: 'basic_chisel',
        name: 'Basic Chisel',
        level: 1,
        speedMultiplier: 1.0,
        price: 0  // Starting tool (free)
    },
    PYRESTONE: {
        id: 'pyrestone_chisel',
        name: 'Pyrestone Chisel',
        level: 15,
        speedMultiplier: 1.2,
        price: 1000
    },
    BLACKIRON: {
        id: 'blackiron_chisel',
        name: 'Blackiron Chisel',
        level: 30,
        speedMultiplier: 1.4,
        price: 5000
    },
    PALE_SHARD: {
        id: 'pale_shard_chisel',
        name: 'Pale Shard Chisel',
        level: 45,
        speedMultiplier: 1.6,
        price: 25000
    },
    SKYIRON: {
        id: 'skyiron_chisel',
        name: 'Skyiron Chisel',
        level: 60,
        speedMultiplier: 1.8,
        price: 100000
    },
    VERDANT: {
        id: 'verdant_chisel',
        name: 'Verdant Chisel',
        level: 75,
        speedMultiplier: 2.0,
        price: 250000
    }
};
